/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
/* eslint-disable */
import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import { CiPlay1 } from "react-icons/ci";
import { IoPlay } from "react-icons/io5";
import { FaRegStopCircle } from "react-icons/fa";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDModal from "components/MDModal";

import DetectionCard from "components/DetectionCard";
import DefaultProjectCard from "examples/Cards/ProjectCards/DefaultProjectCard";

// Images
import masterCardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";
import prototype from "assets/images/oven.png";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

function DevicesView() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [showCircle, setShowCircle] = useState(true);
  const [addDevice, setAddDevice] = useState(false);
  const [device, setDevice] = useState(null);
  const [cookInProgress, setCookInProgress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const sub = JSON.parse(localStorage.getItem("user"))?.sub;
        if (!sub) {
          setDevice(null);
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          "https://int.api.chefaicookware.com/device-service/device/sub",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ sub }),
          }
        );

        if (!response.ok) {
          setDevice(null);
          setIsLoading(false);
          return;
        }

        const data = await response.json();
        if (!data.error) {
          setDevice(data);
        } else {
          setDevice(null);
        }
      } catch (error) {
        console.error("Error fetching device:", error);
        setDevice(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInitiateSensorDataCapture = async () => {
    try {
      const response = await fetch(
        "https://moving-iguana-sweet.ngrok-free.app/device/initiateSensorCapture",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            deviceId: device.deviceId,
            serialNumber: device.serialNumber,
            sub: device.sub,
            action: "start",
            status: "pending",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Error: ${errorData.message}`);
        return;
      }

      console.log("Sensor data capture initiated successfully!");
      const responseData = await response.json();
      console.log(responseData);

      setCookInProgress(true);
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const cancelCook = async () => {
    try {
      const response = await fetch(
        "https://moving-iguana-sweet.ngrok-free.app/device/cancelCookingSession",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            deviceId: device.deviceId,
            serialNumber: device.serialNumber,
            sub: device.sub,
            action: "cancel",
            status: "cancelled",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Error: ${errorData.message}`);
        return;
      }

      console.log("Cook cancelled successfully!");
      const responseData = await response.json();
      console.log(responseData);

      setCookInProgress(false);
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <Container maxWidth="xl">
      <MDBox py={3} px={{ xs: 1, sm: 2, md: 3 }}>
        <Grid container spacing={3}>
          {/* Detection Card - Changes width based on screen size */}
          <Grid item xs={12} lg={device ? 8 : 12}>
            <Card sx={{ height: "100%", minHeight: isMobile ? "300px" : "400px" }}>
              {isLoading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                  <MDTypography variant="h5" color="text">
                    Loading...
                  </MDTypography>
                </MDBox>
              ) : device ? (
                <DetectionCard deviceId={device.deviceId} />
              ) : (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                  p={3}
                  textAlign="center"
                >
                  <Icon fontSize="large" sx={{ mb: 2, fontSize: "3rem", color: "#000" }}>
                    devices_other
                  </Icon>
                  <MDTypography variant="h4" gutterBottom>
                    No Devices Connected
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={3}>
                    Connect your FRY-AI device to get started with automatic cooking detection
                  </MDTypography>
                  <MDButton variant="gradient" color="dark" onClick={handleOpen}>
                    <Icon sx={{ mr: 1 }}>add</Icon>
                    Add New Device
                  </MDButton>
                </MDBox>
              )}
            </Card>
          </Grid>

          {/* Devices Card - Only show if there's a device */}
          {device && (
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: "100%", minHeight: isMobile ? "300px" : "400px" }}>
                <MDBox pt={2} px={2} display="flex" flexDirection="column" alignItems="center">
                  <MDTypography pt={2} pb={3} variant="h4" fontWeight="medium">
                    Your Device
                  </MDTypography>

                  <MDBox width="100%" pb={3}>
                    <MDBox px={isSmallScreen ? 1 : 3}>
                      <DefaultProjectCard
                        image={prototype}
                        label={device?.serialNumber || ""}
                        title={device?.model ? `${device.model}` : "FRY-AI Device"}
                        description="Connected and ready to cook"
                        action={{
                          color: "dark",
                          label: "",
                        }}
                        icon={
                          <>
                            <Tooltip title="Bluetooth Connected">
                              <Icon sx={{ mr: 1, color: "#000" }}>bluetooth_connected</Icon>
                            </Tooltip>
                            <Tooltip title="WiFi Connected">
                              <Icon sx={{ color: "#000" }}>wifi</Icon>
                            </Tooltip>
                          </>
                        }
                      />
                    </MDBox>

                    <MDBox
                      display="flex"
                      flexDirection={isSmallScreen ? "column" : "row"}
                      justifyContent="center"
                      alignItems="center"
                      mt={2}
                    >
                      {cookInProgress ? (
                        <>
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            p={2}
                          >
                            <MDTypography variant="h6" mb={2}>
                              Cook In Progress
                            </MDTypography>
                            <MDButton
                              disabled
                              color="dark"
                              size="large"
                              sx={{ borderRadius: 100, minWidth: "60px", minHeight: "60px" }}
                            >
                              <CiPlay1 size={24} />
                            </MDButton>
                          </MDBox>
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            p={2}
                          >
                            <MDTypography variant="h6" mb={2}>
                              Cancel Cook
                            </MDTypography>
                            <MDButton
                              color="dark"
                              size="large"
                              sx={{ borderRadius: 100, minWidth: "60px", minHeight: "60px" }}
                              onClick={cancelCook}
                            >
                              <FaRegStopCircle size={24} />
                            </MDButton>
                          </MDBox>
                        </>
                      ) : (
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          p={2}
                        >
                          <MDTypography variant="h6" mb={2}>
                            Start Cook
                          </MDTypography>
                          <MDButton
                            color="dark"
                            size="large"
                            sx={{ borderRadius: 100, minWidth: "60px", minHeight: "60px" }}
                            onClick={handleInitiateSensorDataCapture}
                          >
                            <IoPlay size={24} />
                          </MDButton>
                        </MDBox>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      {open && (
        <MDModal
          open={open}
          handleClose={handleClose}
          showCircle={showCircle}
          setAddDevice={setAddDevice}
          setDevice={setDevice}
        />
      )}
    </Container>
  );
}

export default DevicesView;
